/**
 * Le projet d'un onglet né d'un import PDF : le fond ou les calques du PDF, plus
 * le squelette du modèle .lfgr éventuel, dans le repère du FORMAT FINI.
 *
 * Sortie d'EditorView (où elle s'appelait `buildPdfProjectData`) le 2026-10-02
 * pour être exécutable seule : le banc `banc-fond-perdu` la joue sur un vrai
 * gabarit, sans monter l'éditeur.
 */
import { cadrageImport, decalerCalques, modeleSurPageEntiere } from './formatFini';
import { remapTreeIds } from './layerTree';
import { substituerPictos } from './pictoSubstitution';

/**
 * @param {File|{name:string}} file  le PDF importé (seul son nom sert)
 * @param {{mode:string, dimensions:object, image?:string, elements?:Array}} data
 *        la réponse de /api/pdf/import, déjà rapatriée (resolvePdfPayload)
 * @param {Array} skeleton  calques du modèle .lfgr instancié, ou []
 * @param {{width_mm:number, height_mm:number}|null} canvasModele  format du modèle
 */
export function construireProjetPdf(file, data, skeleton, canvasModele = null) {
    // Les calques du PDF sont dans le repère de la PAGE, fond perdu compris ;
    // ceux du document, dans celui du FORMAT FINI (cf. utils/formatFini).
    const w = data.dimensions.width_mm, h = data.dimensions.height_mm;
    const cadrage = cadrageImport(data.dimensions);
    const depuisPdf = [];
    if (data.mode === 'template') {
        depuisPdf.push({ id: crypto.randomUUID(), type: 'image', url: data.image, isAI: false, locked: true, opacity: 100, width: w, height: h, x: 0, y: 0, rotation: 0 });
    } else if (data.mode === 'vector') {
        // Arbre vectoriel (groupes + parentId) : remap préservant la hiérarchie.
        // Les pictogrammes que le serveur a reconnus reçoivent ici leur forme
        // HOMOLOGUÉE, en remplacement des tracés extraits (cf. pictoSubstitution).
        remapTreeIds(substituerPictos(data.elements || [])).forEach(el => depuisPdf.push(el));
    } else if (data.mode === 'extract') {
        (data.elements || []).forEach(el => depuisPdf.push({ ...el, id: crypto.randomUUID() }));
    }
    const elements = decalerCalques(depuisPdf, cadrage.dx, cadrage.dy);
    // Un modèle enregistré sur la page entière d'un PDF du même genre suit le
    // même décalage ; un modèle au format fini se pose tel quel.
    const squelette = modeleSurPageEntiere(canvasModele, data.dimensions)
        ? decalerCalques(skeleton, cadrage.dx, cadrage.dy)
        : skeleton;
    for (const layer of squelette) elements.push({ ...layer, id: crypto.randomUUID() });
    // `ajusterAuContenu` est une consigne portée par l'onglet, pas une
    // action : les calques ne sont mesurables qu'une fois peints, donc à
    // l'activation de l'onglet (cf. hooks/useTabSync).
    return {
        name: file.name.replace(/\.pdf$/i, '') || 'Import',
        canvas: {
            width_mm: cadrage.width_mm,
            height_mm: cadrage.height_mm,
            ...(cadrage.bleed_mm != null ? { bleed_mm: cadrage.bleed_mm } : {}),
        },
        elements,
        ajusterAuContenu: true,
    };
}
